import type { ApiKeyDTO } from '@atlas/types'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { apiKeyStatusPresentation, isApiKeyActionable } from './api-key-status'

export interface ApiKeyTableProps {
	apiKeys: ApiKeyDTO[]
	/** The key currently being revoked, so only its own row shows the pending state. */
	revokingId?: string
	onRevoke: (apiKey: ApiKeyDTO) => void
}

/** Dates arrive as ISO strings on the DTO; `—` where a key has never been used. */
const formatDate = (iso: string | null): string =>
	iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

/**
 * A user's keys, including the ones that no longer work.
 *
 * Revoked and expired keys stay listed with their status: hiding a key the moment
 * it stopped working would leave its owner unable to see what happened to it, and
 * the prefix is how they recognise which stored credential to replace.
 *
 * The full key is never shown — only the prefix the server persists, which is too
 * short to narrow a search for the rest.
 */
export function ApiKeyTable({ apiKeys, revokingId, onRevoke }: ApiKeyTableProps) {
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Key</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Created</TableHead>
					<TableHead>Last used</TableHead>
					<TableHead>Expires</TableHead>
					<TableHead>
						<span className="sr-only">Actions</span>
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{apiKeys.length === 0 ? (
					<TableRow>
						<TableCell colSpan={7} className="py-8 text-center text-ds-text-muted">
							No API keys yet. Create one to use ATLAS from the Figma plugin or a script.
						</TableCell>
					</TableRow>
				) : (
					apiKeys.map((apiKey) => {
						const status = apiKeyStatusPresentation(apiKey.status)
						return (
							<TableRow key={apiKey.apiKeyId}>
								<TableCell className="font-medium text-ds-text">{apiKey.name}</TableCell>
								<TableCell className="font-ds-mono text-ds-sm text-ds-text-muted">{apiKey.keyPrefix}…</TableCell>
								<TableCell>
									<Badge variant={status.variant} size="sm" title={status.hint}>
										{status.label}
									</Badge>
								</TableCell>
								<TableCell className="text-ds-text-muted">{formatDate(apiKey.createdAt)}</TableCell>
								<TableCell className="text-ds-text-muted">{formatDate(apiKey.lastUsedAt)}</TableCell>
								{/* A null expiry means never — the system identity's key, not a user's. */}
								<TableCell className="text-ds-text-muted">
									{apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Never'}
								</TableCell>
								<TableCell className="text-right">
									{isApiKeyActionable(apiKey.status) ? (
										<Button
											type="button"
											variant="danger"
											size="sm"
											className="rounded-ds-full"
											isDisabled={revokingId === apiKey.apiKeyId}
											onPress={() => onRevoke(apiKey)}
										>
											{revokingId === apiKey.apiKeyId ? 'Revoking…' : 'Revoke'}
										</Button>
									) : null}
								</TableCell>
							</TableRow>
						)
					})
				)}
			</TableBody>
		</Table>
	)
}
